import api from './api';

// Transaction types enum
export enum TransactionType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  REFUND = 'REFUND',
  ADJUSTMENT = 'ADJUSTMENT',
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
  ACTIVATION_FEE = 'ACTIVATION_FEE',
}

export interface PendingDeposit {
  referenceNo: string;
  /** MYR amount actually charged (ledger/gateway currency). */
  amount: number;
  /** Set when the payment was priced in a non-MYR display currency (SGD). */
  displayCurrency?: string;
  displayAmount?: number;
  paymentUrl: string | null;
}

/** Pricing snapshot from the server: display-currency price → MYR charge. */
export interface ActivationFee {
  displayAmount: number;
  displayCurrency: string;
  chargeAmountMYR: number;
  conversionRate: number;
  formatted: string;
}

/** Shared shape of deposit / activation-fee payment creation responses. */
export interface DepositPaymentResponse {
  deposit: {
    id: string;
    referenceNo: string;
    /** MYR amount actually charged. */
    amount: number;
    /** Set when priced in a non-MYR display currency (SGD). */
    displayCurrency?: string;
    displayAmount?: number;
    status: string;
  };
  redipayDetails: {
    payment_id: string | null;
    reference_no: string;
    payment_url: string;
    status: string;
    created_date: string;
  };
}

export interface WalletBalance {
  balance: number;
  currency: string;
  formattedBalance: string;
  minimumBalance: number;
  formattedMinimumBalance: string;
  isBelowMinimum: boolean;
  isFrozen: boolean;
  freezeReason?: string;
  isActive: boolean;
  isNewWallet: boolean;
  activationFee?: ActivationFee;
  /** True when the user has a referral discount — promo codes are off. */
  hasReferralDiscount?: boolean;
  pendingDeposit: PendingDeposit | null;
}

export interface WalletTransaction {
  id: string;
  type: string;
  amount: number;
  currency: string;
  amountMYR: number;
  description: string;
  referenceType?: string;
  referenceId?: string;
  balanceBefore: number;
  balanceAfter: number;
  paymentStatus?: 'CREATED' | 'PROCEED' | 'PENDING' | 'PAID' | 'FAILED';
  createdAt: string;
  metadata?: any;
}

export interface TransactionHistoryResponse {
  transactions: WalletTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CurrencyConversionResponse {
  original: {
    amount: number;
    currency: string;
    formatted: string;
  };
  converted: {
    amount: number;
    currency: string;
    formatted: string;
  };
  rate: number;
}

export interface SupportedCurrenciesResponse {
  currencies: Array<{
    code: string;
    name: string;
    symbol: string;
    flag: string;
    rate: number;
  }>;
  minimumBalances: {
    MYR: number;
    SGD: number;
    USD: number;
  };
}

export const walletService = {
  /**
   * Get wallet balance
   */
  async getBalance(currency?: string): Promise<WalletBalance> {
    const params = currency ? `?currency=${currency}` : '';
    const response = await api.get<WalletBalance>(`/wallet/balance${params}`);
    return response.data;
  },

  /**
   * Get transaction history
   */
  async getTransactions(params?: {
    type?: string;
    referenceType?: string;
    page?: number;
    limit?: number;
  }): Promise<TransactionHistoryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.referenceType) queryParams.append('referenceType', params.referenceType);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const response = await api.get<TransactionHistoryResponse>(
      `/wallet/transactions${queryString ? `?${queryString}` : ''}`,
    );
    return response.data;
  },

  /**
   * Convert currency
   */
  async convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ): Promise<CurrencyConversionResponse> {
    const response = await api.post<CurrencyConversionResponse>('/wallet/convert', {
      amount,
      fromCurrency,
      toCurrency,
    });
    return response.data;
  },

  /**
   * Get supported currencies
   */
  async getSupportedCurrencies(): Promise<SupportedCurrenciesResponse> {
    const response = await api.get<SupportedCurrenciesResponse>('/wallet/currencies');
    return response.data;
  },

  /**
   * Update wallet currency preference
   */
  async updateWalletCurrency(currency: string): Promise<{ success: boolean; message: string; currency: string }> {
    const response = await api.put<{ success: boolean; message: string; currency: string }>('/wallet/currency', {
      currency,
    });
    return response.data;
  },

  /**
   * Create wallet deposit. `amount` is denominated in `currency` (the wallet's
   * display currency); the gateway charges the MYR equivalent.
   */
  async createDeposit(amount: number, currency?: string): Promise<DepositPaymentResponse> {
    const response = await api.post('/wallet/deposit', { amount, currency });
    return response.data;
  },

  /**
   * Create membership activation fee payment
   */
  async createActivationFee(promoCode?: string): Promise<DepositPaymentResponse> {
    const response = await api.post('/wallet/activation-fee', promoCode ? { promoCode } : {});
    return response.data;
  },

  /** Live promo check for the wallet page — returns the fee this code gives. */
  async validateActivationPromo(code: string): Promise<{
    valid: boolean;
    reason?: string;
    fee?: ActivationFee;
  }> {
    const response = await api.post('/wallet/activation-fee/validate-promo', { promoCode: code });
    return response.data;
  },
};
